import { useEffect, useMemo, useRef, useState } from "react";
import PageShell from "@/components/PageShell";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, DotsSixVertical, X, Trash, PencilSimple, WarningCircle, Clock, BookmarkSimple, User, ArrowsClockwise } from "@phosphor-icons/react";
import TaskEditDialog from "@/components/TaskEditDialog";
import TaskQuickViewDialog from "@/components/TaskQuickViewDialog";
import { TASK_PRIORITY_LABEL } from "@/lib/enumLabels";
import { toast } from "sonner";

const COLUMNS = [
  { key: "backlog", label: "Backlog" },
  { key: "todo", label: "To do" },
  { key: "in_progress", label: "In progress" },
  { key: "review", label: "Review" },
  { key: "done", label: "Done" },
];
const PRIORITIES = ["urgent", "high", "medium", "low"];
/*
  PRIORITY_TONE — text color for the priority label chip.
    urgent  → destructive (semantic red, both themes)
    high    → text-primary (Navy in light, lifted Navy in dark via .text-primary override)
    medium  → foreground (Black in light, Ivory in dark) — stays scannable
    low     → muted-foreground (Stone deep light / Stone bright dark)
  PRIORITY_ACCENT — left-rail hsl reference used inline so the rail follows theme.
    Rail is the ONLY strong color on the card; the surface stays Stone/Ivory.
*/
const PRIORITY_TONE = { urgent: "text-destructive", high: "text-primary", medium: "text-foreground", low: "text-muted-foreground" };
const PRIORITY_ACCENT = {
  urgent: "hsl(var(--destructive))",
  high:   "hsl(var(--primary-fg))",
  medium: "hsl(var(--muted-foreground) / 0.55)",
  low:    "hsl(var(--border))",
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [engs, setEngs] = useState([]);
  const [users, setUsers] = useState([]);
  // Iter 85.34 · Cache of every Activity Briefing so the New-task dialog
  // can offer an optional Activity picker. Populated once on load; the
  // picker filters this list to the currently-selected Engagement (or
  // Client, if no engagement) to prevent accidental cross-engagement
  // Activity associations.
  const [activities, setActivities] = useState([]);
  // Iter 85.4 · Task Board Chips — cache of every contact across every
  // client, keyed by contact id. Lets the Kanban card render name/initials
  // from a bound contact_id without an extra network hop per card.
  const [contactById, setContactById] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", engagement_id: "", assignee_id: "", priority: "medium", due_date: "", estimated_hours: "", activity_briefing_id: "" });
  const [dragging, setDragging] = useState(null);
  const [engFilter, setEngFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  // Persistent "pinned" card so a partner can talk through a specific task
  // without needing to hover. Click again (or ESC) to release. Single-select.
  const [selectedId, setSelectedId] = useState(null);
  // Iter 85.63 · Shared TaskEditDialog integration + archived-toggle
  // for the firm-wide board.
  const [editingTask, setEditingTask] = useState(null);
  // Iter 85.76 · Task Quick View — plain click opens a read-only detail
  // view. Editing is an explicit action inside the Quick View.
  const [quickViewTask, setQuickViewTask] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  // Iter 85.73 · Overdue filter chip (b) — flips on with one tap; also
  // implicit when a saved chip carries overdueOnly: true.
  const [overdueOnly, setOverdueOnly] = useState(false);
  // Iter 85.73 · Multi-select for bulk actions (g). A Set of task ids;
  // shift-click extends selection, ctrl/cmd-click toggles a single task,
  // plain click clears the selection and pins the single card as before.
  const [multiSelect, setMultiSelect] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Iter 85.73 · Saved filter chips (f) live in localStorage. Shape:
  //   [{ id, name, filters: {engFilter, priorityFilter, assigneeFilter, overdueOnly} }]
  const [savedFilters, setSavedFilters] = useState(() => {
    try {
      const raw = localStorage.getItem("yadid.tasks.saved_filters");
      return raw ? JSON.parse(raw) : [];
    } catch (_) { return []; }
  });
  // Iter 85.73 · Quick-add per column (d). Column key currently in
  // inline-compose mode; only one column composes at a time.
  const [quickAddCol, setQuickAddCol] = useState(null);
  const [quickAddTitle, setQuickAddTitle] = useState("");
  const [quickAddBusy, setQuickAddBusy] = useState(false);
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const openParamConsumedRef = useRef(false);

  const load = () => {
    // Iter 85.63 · Firm-wide board respects the 30-day retention window
    // server-side. Toggling `Show archived` re-requests with the
    // include_archived flag so archived done-tasks remain reachable.
    api.get("/tasks", { params: showArchived ? { include_archived: 1 } : {} })
      .then((r) => setTasks(r.data));
    api.get("/engagements").then((r) => setEngs(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    // Iter 85.34 · Load every Activity Briefing for the manual-assignment
    // picker in the New-task dialog. Small firm, small payload; keeping
    // this in memory avoids a fresh fetch each time the dialog opens.
    api.get("/notes", { params: { note_type: "activity_briefing" } })
      .then((r) => setActivities(r.data || []))
      .catch(() => setActivities([]));
    // Iter 85.4 · Task Board Chips — build contact-id → contact lookup
    // from every client so we can render initials on task cards without
    // extra fetches per card. Small firm, small payload.
    api.get("/clients").then((r) => {
      const map = {};
      (r.data || []).forEach((c) => {
        (c.stakeholders || []).forEach((s) => {
          if (s && s.id) map[s.id] = { name: s.name, title: s.title, client_id: c.id };
        });
      });
      setContactById(map);
    }).catch(() => setContactById({}));
  };
  useEffect(() => { load(); }, [showArchived]);

  // ── Deep-link · `?open=<task_id>` ──
  // Signals-from-the-field Open-tasks rows navigate to /tasks?open=<id>.
  // On mount, if the param is present, pin that task and scroll it into
  // view. Param stripped after consumption. Runs once tasks have loaded
  // so the scroll target actually exists.
  useEffect(() => {
    if (openParamConsumedRef.current) return;
    const openId = searchParams.get("open");
    if (!openId) return;
    if (!tasks.some((t) => t.id === openId)) return;
    openParamConsumedRef.current = true;
    setSelectedId(openId);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="task-card-${openId}"], [data-task-id="${openId}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [searchParams, tasks, setSearchParams]);

  const submit = async () => {
    if (!form.title) return toast.error("Title required");
    // Iter 85.34 · If the chosen Activity Briefing doesn't belong to the
    // selected engagement, silently drop the association to prevent an
    // accidental cross-engagement mis-tag. The picker already filters
    // this list, but guard defensively here in case the state races.
    let activityId = form.activity_briefing_id || null;
    if (activityId) {
      const act = activities.find((a) => a.id === activityId);
      if (act && form.engagement_id && act.engagement_id !== form.engagement_id) {
        activityId = null;
      }
    }
    await api.post("/tasks", {
      ...form,
      engagement_id: form.engagement_id || null,
      assignee_id: form.assignee_id || null,
      estimated_hours: form.estimated_hours ? parseFloat(form.estimated_hours) : null,
      activity_briefing_id: activityId,
      // If the Task is bound to an Activity Briefing, inherit the
      // activity's Client automatically so the canonical Client → Task
      // link is not lost when the user picks an activity without an
      // explicit engagement.
      client_id: (activityId ? (activities.find((a) => a.id === activityId)?.client_id) : null) || null,
    });
    setForm({ title: "", description: "", engagement_id: "", assignee_id: "", priority: "medium", due_date: "", estimated_hours: "", activity_briefing_id: "" });
    setOpen(false);
    toast.success("Task created");
    load();
  };

  const moveTask = async (task, newStatus) => {
    if (task.status === newStatus) return;
    setTasks((prev) => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    await api.put(`/tasks/${task.id}`, { status: newStatus });
  };

  // Iter 85.62 · Delete a task from the active workflow. Uses the existing
  // DELETE /tasks/{id} endpoint which soft-deletes; source_briefing_id /
  // source_activity_briefing_id provenance stays intact on the record.
  const deleteTask = async (task) => {
    if (!confirm(`Delete "${task.title}"?`)) return;
    const prev = tasks;
    setTasks((p) => p.filter((t) => t.id !== task.id));
    if (selectedId === task.id) setSelectedId(null);
    try {
      await api.delete(`/tasks/${task.id}`);
    } catch (err) {
      setTasks(prev);
      // Restore selection on failure
      // eslint-disable-next-line no-console
      console.error("delete task failed", err);
    }
  };

  // Iter 85.73 · Overdue / due-soon signals (b). We compute against
  // *today* (local), which is fine for a firm working in one time zone;
  // done and archived tasks never flag. Due-soon = within the next 7
  // calendar days (excluding today's overdue window).
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const soonThreshold = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  }, []);
  const taskUrgency = (t) => {
    if (!t.due_date) return "none";
    if ((t.status || "").toLowerCase() === "done") return "none";
    if (t.due_date < todayISO) return "overdue";
    if (t.due_date <= soonThreshold) return "soon";
    return "none";
  };

  // Compose all active filters. Any filter set to "all" is skipped. Filters
  // are AND-ed so partners can narrow the board with any combination.
  const filtered = tasks.filter(t => {
    if (engFilter !== "all" && t.engagement_id !== engFilter) return false;
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    if (assigneeFilter !== "all") {
      if (assigneeFilter === "unassigned" ? t.assignee_id : t.assignee_id !== assigneeFilter) return false;
    }
    if (overdueOnly && taskUrgency(t) !== "overdue") return false;
    return true;
  });
  const activeFilterCount = (engFilter !== "all" ? 1 : 0) + (priorityFilter !== "all" ? 1 : 0) + (assigneeFilter !== "all" ? 1 : 0) + (overdueOnly ? 1 : 0);
  const clearFilters = () => { setEngFilter("all"); setPriorityFilter("all"); setAssigneeFilter("all"); setOverdueOnly(false); };
  const engName = (eid) => engs.find(e => e.id === eid)?.name;
  const userName = (uid) => users.find(u => u.id === uid)?.name;

  // ── Iter 85.73 · Saved filter chips (f) ─────────────────────────────
  const applySavedFilters = (f) => {
    if (!f) return;
    setEngFilter(f.engFilter || "all");
    setPriorityFilter(f.priorityFilter || "all");
    setAssigneeFilter(f.assigneeFilter || "all");
    setOverdueOnly(!!f.overdueOnly);
  };
  const persistSavedFilters = (next) => {
    setSavedFilters(next);
    try { localStorage.setItem("yadid.tasks.saved_filters", JSON.stringify(next)); } catch (_) {}
  };
  const saveCurrentFilterAs = () => {
    if (activeFilterCount === 0) { toast.error("Nothing to save — pick a filter first"); return; }
    const name = (prompt("Name this filter (e.g. 'Overdue for J. Rojo'):") || "").trim();
    if (!name) return;
    const chip = {
      id: `f-${Date.now()}`,
      name,
      filters: {
        engFilter, priorityFilter, assigneeFilter, overdueOnly,
      },
    };
    persistSavedFilters([...savedFilters, chip]);
    toast.success(`Saved '${name}'`);
  };
  const deleteSavedFilter = (id) => persistSavedFilters(savedFilters.filter((c) => c.id !== id));
  const myTasksActive =
    !!currentUser && assigneeFilter === currentUser.id && !overdueOnly && engFilter === "all" && priorityFilter === "all";
  const applyMyTasks = () => {
    if (!currentUser) return;
    setAssigneeFilter(currentUser.id);
    setEngFilter("all");
    setPriorityFilter("all");
    setOverdueOnly(false);
  };

  // ── Iter 85.73 · Multi-select helpers (g) ───────────────────────────
  const isMultiSelected = (id) => multiSelect.has(id);
  const clearMultiSelect = () => setMultiSelect(new Set());
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { clearMultiSelect(); setSelectedId(null); setQuickAddCol(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const bulkApply = async (payload, verb) => {
    if (multiSelect.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    const ids = [...multiSelect];
    const snapshot = new Map(tasks.filter((t) => multiSelect.has(t.id)).map((t) => [t.id, t]));
    // Optimistic local update
    setTasks((prev) => prev.map((t) => (multiSelect.has(t.id) ? { ...t, ...payload } : t)));
    // Iter 85.73 · Per-id failure tracking so a partial failure only
    // rolls back the specific ids that errored — the successful ids
    // stay updated locally and the partner gets an actionable toast.
    const results = await Promise.allSettled(
      ids.map((id) => api.put(`/tasks/${id}`, payload).then(() => id))
    );
    const failedIds = results
      .filter((r) => r.status === "rejected")
      .map((r, i) => ids[i]);
    setBulkBusy(false);
    if (failedIds.length === 0) {
      toast.success(`${verb} ${ids.length} task${ids.length === 1 ? "" : "s"}`);
      clearMultiSelect();
      load();
      return;
    }
    // Roll back only the failed ids to their pre-mutation snapshot.
    setTasks((prev) => prev.map((t) => (snapshot.has(t.id) && failedIds.includes(t.id) ? snapshot.get(t.id) : t)));
    const succeeded = ids.length - failedIds.length;
    if (succeeded > 0) {
      toast.warning(`${verb} ${succeeded} of ${ids.length} · ${failedIds.length} failed and were reverted`);
    } else {
      toast.error(`${verb.toLowerCase()} failed on all ${ids.length} tasks`);
    }
    load();
  };
  const bulkDelete = async () => {
    if (multiSelect.size === 0 || bulkBusy) return;
    if (!confirm(`Delete ${multiSelect.size} task${multiSelect.size === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkBusy(true);
    const ids = [...multiSelect];
    setTasks((prev) => prev.filter((t) => !multiSelect.has(t.id)));
    try {
      await Promise.all(ids.map((id) => api.delete(`/tasks/${id}`)));
      toast.success(`Deleted ${ids.length} task${ids.length === 1 ? "" : "s"}`);
      clearMultiSelect();
      load();
    } catch (e) {
      toast.error("Bulk delete failed — reloading");
      load();
    } finally { setBulkBusy(false); }
  };

  // ── Iter 85.73 · Inline quick-add (d) ───────────────────────────────
  const quickAddSubmit = async (colKey) => {
    const title = (quickAddTitle || "").trim();
    if (!title || quickAddBusy) return;
    setQuickAddBusy(true);
    try {
      // Inherit any active engagement filter so the quick task lands on
      // the right engagement — reduces the "unbound tasks piling up" bug
      // partners kept hitting when they used the top-level New Task dialog.
      const payload = {
        title,
        status: colKey,
        priority: "medium",
        engagement_id: engFilter !== "all" ? engFilter : null,
        assignee_id: assigneeFilter !== "all" && assigneeFilter !== "unassigned" ? assigneeFilter : null,
      };
      await api.post("/tasks", payload);
      setQuickAddTitle("");
      // Keep the composer open so the partner can add several in a row —
      // Esc / blur closes it explicitly.
      toast.success(`Added to ${colKey.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally { setQuickAddBusy(false); }
  };

  return (
    <PageShell size="board">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">Execution</div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight">Tasks</h1>
          <p className="text-sm text-muted-foreground mt-1">Firm-wide execution board · drag cards to update status</p>
        </div>
        <div className="flex gap-3 items-center">
          {/* Iter 85.63 · Archived toggle — reveals done-tasks older
              than 30 days that dropped off the board via the server's
              retention filter. Archived tasks are never deleted; this
              toggle just re-requests with include_archived=1. */}
          <label className="flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground cursor-pointer" data-testid="tasks-show-archived-label">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="tasks-show-archived-toggle"
              className="accent-primary"
            />
            Show archived
          </label>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-task-btn" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"><Plus size={14} weight="bold" className="mr-2" /> New task</Button></DialogTrigger>
            <DialogContent className="rounded-none bg-card">
              <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input data-testid="task-title" placeholder="Title" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} className="rounded-none bg-secondary/40" />
                <Textarea placeholder="Description" rows={3} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} className="rounded-none bg-secondary/40" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Select value={form.engagement_id || "none"} onValueChange={(v)=>setForm({...form,engagement_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="rounded-none bg-secondary/40"><SelectValue placeholder="Engagement" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">(No engagement)</SelectItem>
                      {engs.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={form.assignee_id || "none"} onValueChange={(v)=>setForm({...form,assignee_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="rounded-none bg-secondary/40"><SelectValue placeholder="Assignee" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">(Unassigned)</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={form.priority} onValueChange={(v)=>setForm({...form,priority:v})}>
                    <SelectTrigger className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
                    <SelectContent>{["low","medium","high","urgent"].map(p=><SelectItem key={p} value={p}>{TASK_PRIORITY_LABEL[p]}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="date" value={form.due_date} onChange={(e)=>setForm({...form,due_date:e.target.value})} className="rounded-none bg-secondary/40" />
                  <Input type="number" step="0.5" placeholder="Est. hours" value={form.estimated_hours} onChange={(e)=>setForm({...form,estimated_hours:e.target.value})} className="rounded-none bg-secondary/40 col-span-2" />
                </div>
                {/* Iter 85.34 · Optional Activity Briefing association.
                    Rendered only when an Engagement is selected — an
                    activity always belongs to an engagement, so scoping
                    the picker keeps cross-engagement mis-tags impossible.
                    Empty option preserves the "no activity" case for
                    engagement-level tasks that aren't tied to a specific
                    activity. */}
                {form.engagement_id && (() => {
                  const scoped = activities.filter((a) => a.engagement_id === form.engagement_id);
                  if (scoped.length === 0) return null;
                  return (
                    <div>
                      <div className="text-[0.5938rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                        Activity briefing (optional)
                      </div>
                      <Select
                        value={form.activity_briefing_id || "none"}
                        onValueChange={(v) => setForm({ ...form, activity_briefing_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger data-testid="task-activity-select" className="rounded-none bg-secondary/40"><SelectValue placeholder="(No activity)" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">(No activity)</SelectItem>
                          {scoped.map((a) => (
                            <SelectItem key={a.id} value={a.id} data-testid={`task-activity-option-${a.id}`}>
                              {a.title}{a.activity_date ? ` · ${a.activity_date}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })()}
              </div>
              <DialogFooter><Button data-testid="task-submit" onClick={submit} className="rounded-none bg-primary text-primary-foreground">Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/*
        Board filter bar — a single row of narrow selects so partners can
        carve the board (engagement × priority × assignee) without opening
        each card. Filters are AND-ed. A subtle count + one-tap "Clear" chip
        makes it obvious when the board is filtered.
      */}
      <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="tasks-filter-bar">
        <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-eyebrow mr-1">Filter</div>
        <Select value={engFilter} onValueChange={setEngFilter}>
          <SelectTrigger data-testid="filter-engagement" className="rounded-none bg-secondary/40 h-8 w-52 text-xs"><SelectValue placeholder="All engagements" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All engagements</SelectItem>
            {engs.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger data-testid="filter-priority" className="rounded-none bg-secondary/40 h-8 w-36 text-xs"><SelectValue placeholder="Any priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any priority</SelectItem>
            {PRIORITIES.map(p => <SelectItem key={p} value={p}>{TASK_PRIORITY_LABEL[p]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger data-testid="filter-assignee" className="rounded-none bg-secondary/40 h-8 w-44 text-xs"><SelectValue placeholder="Any assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any assignee</SelectItem>
            <SelectItem value="unassigned">Unassigned</SelectItem>
            {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            data-testid="filter-clear"
            className="rounded-none border border-border h-8 px-2 flex items-center gap-1 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground hover:border-ring"
          >
            <X size={10} weight="bold" /> Clear · {activeFilterCount}
          </button>
        )}
        <span className="text-[0.625rem] font-mono text-muted-foreground ml-auto tabular-nums">{filtered.length} of {tasks.length}</span>
      </div>

      {/* Iter 85.73 · Quick-filter chip row — one-tap access to the two
          most common carves (My tasks, Overdue) plus any saved-filter chips
          the partner has stashed away. Sits between the filter bar and the
          board so activating a chip doesn't push the columns around. */}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="tasks-quickchips">
        {currentUser && (
          <button
            type="button"
            onClick={() => (myTasksActive ? setAssigneeFilter("all") : applyMyTasks())}
            data-testid="chip-my-tasks"
            className={`rounded-none border h-7 px-2.5 flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest ${
              myTasksActive
                ? "border-primary text-primary bg-primary/10"
                : "border-border text-muted-foreground hover:text-foreground hover:border-ring"
            }`}
          >
            <User size={11} weight="bold" /> My tasks
          </button>
        )}
        <button
          type="button"
          onClick={() => setOverdueOnly((v) => !v)}
          data-testid="chip-overdue"
          className={`rounded-none border h-7 px-2.5 flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest ${
            overdueOnly
              ? "border-destructive text-destructive bg-destructive/10"
              : "border-border text-muted-foreground hover:text-foreground hover:border-ring"
          }`}
        >
          <WarningCircle size={11} weight="bold" /> Overdue only
        </button>
        {savedFilters.map((chip) => {
          const f = chip.filters || {};
          const active = (
            (f.engFilter || "all") === engFilter &&
            (f.priorityFilter || "all") === priorityFilter &&
            (f.assigneeFilter || "all") === assigneeFilter &&
            !!f.overdueOnly === overdueOnly
          );
          return (
            <span
              key={chip.id}
              className={`rounded-none border h-7 flex items-center text-[0.625rem] font-mono uppercase tracking-widest ${
                active
                  ? "border-primary text-primary bg-primary/10"
                  : "border-border text-muted-foreground"
              }`}
              data-testid={`chip-saved-${chip.id}`}
            >
              <button
                type="button"
                onClick={() => applySavedFilters(chip.filters)}
                className="flex items-center gap-1.5 pl-2.5 pr-1.5 hover:text-foreground"
                title={`Load '${chip.name}'`}
              >
                <BookmarkSimple size={11} weight="bold" /> {chip.name}
              </button>
              <button
                type="button"
                onClick={() => deleteSavedFilter(chip.id)}
                data-testid={`chip-saved-${chip.id}-delete`}
                className="border-l border-border/60 px-1 h-full hover:text-destructive"
                title="Remove saved filter"
              >
                <X size={9} weight="bold" />
              </button>
            </span>
          );
        })}
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={saveCurrentFilterAs}
            data-testid="chip-save-current"
            className="rounded-none border border-dashed border-border h-7 px-2.5 flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary hover:border-primary"
            title="Save this filter combo as a chip"
          >
            <Plus size={10} weight="bold" /> Save this filter
          </button>
        )}
      </div>

      {/* Iter 85.73 · Bulk actions bar (g) — appears only when 1+ tasks
          are multi-selected. Sits just above the board so the partner
          can operate on the selection without losing the columns
          visual context. Reassign, reschedule, move status, or delete. */}
      {multiSelect.size > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 border border-primary bg-primary/5 px-3 py-2" data-testid="tasks-bulk-bar">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">
            {multiSelect.size} selected
          </div>
          <div className="h-4 w-px bg-primary/30 mx-1" />
          <Select onValueChange={(v) => bulkApply({ status: v }, "Moved")}>
            <SelectTrigger data-testid="bulk-move-status" className="rounded-none bg-transparent border-primary/40 text-primary h-7 w-40 text-[0.625rem] font-mono uppercase tracking-widest"><SelectValue placeholder="Move to…" /></SelectTrigger>
            <SelectContent>
              {COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select onValueChange={(v) => bulkApply({ assignee_id: v === "__unassigned__" ? null : v }, "Reassigned")}>
            <SelectTrigger data-testid="bulk-reassign" className="rounded-none bg-transparent border-primary/40 text-primary h-7 w-40 text-[0.625rem] font-mono uppercase tracking-widest"><SelectValue placeholder="Reassign…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__unassigned__">Unassigned</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select onValueChange={(v) => bulkApply({ priority: v }, "Repriced")}>
            <SelectTrigger data-testid="bulk-priority" className="rounded-none bg-transparent border-primary/40 text-primary h-7 w-36 text-[0.625rem] font-mono uppercase tracking-widest"><SelectValue placeholder="Priority…" /></SelectTrigger>
            <SelectContent>
              {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{TASK_PRIORITY_LABEL[p]}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="inline-flex items-center gap-1">
            <input
              type="date"
              data-testid="bulk-reschedule"
              onChange={(e) => e.target.value && bulkApply({ due_date: e.target.value }, "Rescheduled")}
              className="rounded-none bg-transparent border border-primary/40 h-7 px-2 text-[0.6875rem] font-mono text-primary outline-none"
              title="Reschedule selected"
            />
          </div>
          <button
            type="button"
            onClick={bulkDelete}
            disabled={bulkBusy}
            data-testid="bulk-delete"
            className="rounded-none border border-destructive/60 text-destructive hover:bg-destructive/10 h-7 px-2.5 flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest disabled:opacity-40"
          >
            <Trash size={11} weight="bold" /> Delete
          </button>
          <button
            type="button"
            onClick={clearMultiSelect}
            data-testid="bulk-clear"
            className="rounded-none border border-border h-7 px-2.5 flex items-center gap-1.5 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground ml-auto"
          >
            <X size={11} weight="bold" /> Clear
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {COLUMNS.map((col) => {
          const colTasks = filtered.filter(t => t.status === col.key);
          return (
            <div
              key={col.key}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragging) { moveTask(dragging, col.key); setDragging(null); } }}
              className="border border-border bg-card min-h-[400px]"
              data-testid={`kanban-col-${col.key}`}
            >
              <div className="px-3 py-2 border-b border-border flex items-center justify-between sticky top-0 bg-card">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">{col.label}</div>
                <div className="text-[0.625rem] font-mono text-primary">{colTasks.length}</div>
              </div>
              <div className="p-2 space-y-2">
                {colTasks.map((t) => {
                  const isDragging = dragging?.id === t.id;
                  const isSelected = selectedId === t.id;
                  const isMulti = isMultiSelected(t.id);
                  const urgency = taskUrgency(t);
                  const railColor =
                    urgency === "overdue" ? "hsl(var(--destructive))" :
                    (PRIORITY_ACCENT[t.priority] || PRIORITY_ACCENT.medium);
                  const onCardClick = (ev) => {
                    // Iter 85.73 · Modifier-aware selection.
                    //   plain click       → toggle single-pin (unchanged legacy behaviour)
                    //   ctrl/cmd + click  → toggle in multi-select
                    //   shift + click     → toggle in multi-select (linear range would be nicer
                    //                       but requires a stable card order; toggle is a fine v1)
                    if (ev.metaKey || ev.ctrlKey || ev.shiftKey) {
                      ev.preventDefault();
                      setMultiSelect((prev) => {
                        const next = new Set(prev);
                        if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                        return next;
                      });
                      setSelectedId(null);
                      return;
                    }
                    // If a bulk selection is already active, plain click on
                    // a card also toggles it in the multiset so the partner
                    // can build the selection with the mouse. Clicking
                    // outside the board (or Esc) clears the multiset.
                    if (multiSelect.size > 0) {
                      setMultiSelect((prev) => {
                        const next = new Set(prev);
                        if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                        return next;
                      });
                      return;
                    }
                    // Iter 85.76 · Plain click → open Quick View. Pin
                    // selection state stays intact for the visual accent
                    // on the card while the dialog is open.
                    setSelectedId(t.id);
                    setQuickViewTask(t);
                  };
                  return (
                    <div
                      key={t.id}
                      draggable
                      tabIndex={0}
                      role="button"
                      aria-pressed={isSelected || isMulti}
                      onDragStart={() => setDragging(t)}
                      onDragEnd={() => setDragging(null)}
                      onClick={onCardClick}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onCardClick(e); } }}
                      className={`kanban-card group p-3 cursor-grab active:cursor-grabbing ${isDragging ? "is-dragging" : ""} ${isSelected ? "is-selected" : ""} ${isMulti ? "ring-2 ring-primary" : ""}`}
                      style={{ borderLeft: `2px solid ${railColor}` }}
                      data-testid={`task-card-${t.id}`}
                      data-selected={isSelected ? "true" : "false"}
                      data-multi-selected={isMulti ? "true" : "false"}
                      data-urgency={urgency}
                    >
                      <div className="flex items-start gap-2">
                        <DotsSixVertical size={12} className="text-muted-foreground mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div
                              className="text-sm font-medium leading-snug text-foreground flex-1 min-w-0 line-clamp-2 break-words"
                              title={t.title}
                              data-testid={`task-card-title-${t.id}`}
                            >
                              {t.title}
                            </div>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 shrink-0 transition-opacity">
                              <button
                                onClick={(ev) => { ev.stopPropagation(); setEditingTask(t); }}
                                data-testid={`task-edit-${t.id}`}
                                className="text-muted-foreground hover:text-primary p-0.5 -mt-0.5"
                                aria-label={`Edit ${t.title}`}
                                title="Edit task"
                              >
                                <PencilSimple size={12} weight="bold" />
                              </button>
                              <button
                                onClick={(ev) => { ev.stopPropagation(); deleteTask(t); }}
                                data-testid={`task-delete-${t.id}`}
                                className="text-muted-foreground hover:text-destructive p-0.5 -mt-0.5"
                                aria-label={`Delete ${t.title}`}
                                title="Delete task"
                              >
                                <Trash size={12} weight="bold" />
                              </button>
                            </div>
                          </div>
                          {t.engagement_id && <div className="text-[0.625rem] font-mono uppercase text-muted-foreground tracking-widest mt-1 truncate" title={engName(t.engagement_id) || undefined}>{engName(t.engagement_id) || "—"}</div>}
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {urgency === "overdue" && (
                              <span
                                className="inline-flex items-center gap-1 border border-destructive/60 text-destructive px-1.5 py-0.5 text-[0.5625rem] font-mono uppercase tracking-widest"
                                data-testid={`task-card-overdue-${t.id}`}
                                title={`Due ${t.due_date} — past due`}
                              >
                                <WarningCircle size={9} weight="fill" /> Overdue
                              </span>
                            )}
                            {urgency === "soon" && (
                              <span
                                className="inline-flex items-center gap-1 border border-amber-500/60 text-amber-500 px-1.5 py-0.5 text-[0.5625rem] font-mono uppercase tracking-widest"
                                data-testid={`task-card-duesoon-${t.id}`}
                                title={`Due ${t.due_date}`}
                              >
                                <Clock size={9} weight="bold" /> Due soon
                              </span>
                            )}
                            <span className={`text-[0.625rem] font-mono uppercase tracking-widest ${PRIORITY_TONE[t.priority]}`}>{TASK_PRIORITY_LABEL[t.priority] || t.priority}</span>
                            {t.due_date && <span className="text-[0.625rem] font-mono text-muted-foreground">· {t.due_date}</span>}
                            {t.assignee_id && <span className="text-[0.625rem] font-mono text-foreground">· {userName(t.assignee_id)}</span>}
                          </div>
                          {/* Iter 85.4 · Task Board Chips — show initials of any contacts
                              bound to this task so the Kanban reveals who each task
                              touches at a glance. Resolves names via the parent client's
                              stakeholders cache (populated when Tasks page loads). */}
                          {Array.isArray(t.contact_ids) && t.contact_ids.length > 0 && (
                            <div className="mt-2 flex items-center gap-1" data-testid={`task-card-contacts-${t.id}`}>
                              {t.contact_ids.slice(0, 3).map((cid) => {
                                const c = contactById[cid];
                                const initials = (c?.name || "?")
                                  .split(/\s+/).filter(Boolean).slice(0, 2)
                                  .map((w) => w[0]?.toUpperCase()).join("");
                                return (
                                  <span
                                    key={cid}
                                    className="inline-flex items-center justify-center w-5 h-5 border border-primary/40 bg-primary/10 text-primary text-[0.5625rem] font-mono font-bold"
                                    title={c?.name || "Unknown contact"}
                                    data-testid={`task-card-contact-chip-${t.id}-${cid}`}
                                  >
                                    {initials || "?"}
                                  </span>
                                );
                              })}
                              {t.contact_ids.length > 3 && (
                                <span className="text-[0.625rem] font-mono text-muted-foreground">+{t.contact_ids.length - 3}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {colTasks.length === 0 && <div className="text-[0.625rem] font-mono uppercase text-muted-foreground text-center py-6">Empty</div>}
                {/* Iter 85.73 · Inline quick-add (d). Enter creates the task
                    with status = column key, priority = medium, and inherits
                    the active engagement/assignee filter so partners can
                    triage a fresh incoming task into place with one keystroke. */}
                {quickAddCol === col.key ? (
                  <div className="border border-dashed border-primary bg-background/50 p-2" data-testid={`quick-add-${col.key}-form`}>
                    <input
                      autoFocus
                      type="text"
                      value={quickAddTitle}
                      onChange={(e) => setQuickAddTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); quickAddSubmit(col.key); }
                        if (e.key === "Escape") { e.preventDefault(); setQuickAddCol(null); setQuickAddTitle(""); }
                      }}
                      onBlur={() => {
                        // Small delay so the Cancel/Add buttons below (if
                        // we render any) get their click first.
                        setTimeout(() => {
                          if (!quickAddTitle.trim()) setQuickAddCol(null);
                        }, 120);
                      }}
                      data-testid={`quick-add-${col.key}-input`}
                      placeholder="Title · Enter to add · Esc to cancel"
                      className="w-full bg-transparent text-sm outline-none font-medium placeholder:text-muted-foreground placeholder:font-normal"
                      disabled={quickAddBusy}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setQuickAddCol(col.key); setQuickAddTitle(""); }}
                    data-testid={`quick-add-${col.key}-open`}
                    className="w-full text-left text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary py-1.5 px-2 border border-dashed border-transparent hover:border-primary/40 flex items-center gap-1.5"
                  >
                    <Plus size={11} weight="bold" /> Add task
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Iter 85.63 · Shared canonical Task editor. Same dialog used by
          Engagement Tasks tab and every other Task surface so every save
          hits PUT /api/tasks/{id} — the server is the single source of
          truth. */}
      <TaskEditDialog
        open={!!editingTask}
        onOpenChange={(o) => { if (!o) setEditingTask(null); }}
        task={editingTask}
        engagements={engs}
        activities={activities}
        users={users}
        onSaved={() => load()}
        onDeleted={() => load()}
      />
      <TaskQuickViewDialog
        task={quickViewTask}
        open={!!quickViewTask}
        onOpenChange={(o) => { if (!o) setQuickViewTask(null); }}
        engagements={engs}
        users={users}
        onEdit={(t) => { setQuickViewTask(null); setEditingTask(t); }}
        onTaskChange={() => load()}
      />
    </PageShell>
  );
}
